<template>
  <div class="chat-agent-side">
    <div class="agent-side-search">
      <el-input placeholder="搜索智能体" v-model="searchText" size="small">
        <i class="el-icon-search el-input__icon" slot="prefix"></i>
      </el-input>
    </div>
    
    <div class="agent-side-scrollbar">
      <el-scrollbar style="height: 100%;" v-loading="loading" element-loading-text="加载中...">
        <div class="agent-side-list">
          <!-- 用户智能体 -->
          <div class="agent-section" v-if="filteredMyAgents.length > 0">
            <div class="section-title">{{ agentSectionTitle }}</div>
            <div v-for="agent in filteredMyAgents" :key="agent.agentId">
              <chat-agent-member
                class="agent-side-member"
                :agent="agent"
                :current-group="group"
                @start-chat="handleStartChat"
                @action="handleAgentAction">
              </chat-agent-member>
            </div>
          </div>

          <!-- 共享智能体 -->
          <div class="agent-section" v-if="filteredSharedAgents.length > 0">
            <div class="section-title">共享智能体</div>
            <div v-for="agent in filteredSharedAgents" :key="agent.agentId">
              <chat-agent-member
                class="agent-side-member"
                :agent="agent"
                :current-group="group"
                @start-chat="handleStartChat"
                @action="handleAgentAction">
              </chat-agent-member>
            </div>
          </div>
          
          <!-- 空状态 -->
          <div v-if="!loading && filteredMyAgents.length === 0 && filteredSharedAgents.length === 0" class="empty-state">
            <i class="el-icon-user"></i>
            <p>暂无可用智能体</p>
            <el-button size="small" type="text" @click="refreshAgents">刷新</el-button>
          </div>
        </div>
      </el-scrollbar>
    </div>

    <!-- 分享智能体对话框 -->
    <share-agent-dialog
      v-if="shareDialogVisible && currentShareAgent.agentId"
      :visible.sync="shareDialogVisible"
      :agent-id="currentShareAgent.agentId"
      :agent-name="currentShareAgent.agentName"
      :agent-avatar="currentShareAgent.avatarUrl"
      @close="shareDialogVisible = false"
    />
  </div>
</template>

<script>
import ChatAgentMember from './ChatAgentMember.vue';
import ShareAgentDialog from '../agent/ShareAgentDialog.vue';

export default {
  name: "chatAgentSide",
  components: {
    ChatAgentMember,
    ShareAgentDialog
  },
  props: {
    chat: Object,
    group: Object,
    groupMembers: Array,
    agents: {  // 从父组件接收智能体列表
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchText: "",
      loading: false,  // 添加缺失的 loading 属性
      shareDialogVisible: false,  // 分享对话框显示状态
      currentShareAgent: {}  // 当前要分享的智能体
    }
  },
  computed: {
    // 直接使用父组件传入的 agents，并过滤已发布的智能体
    filteredMyAgents() {
      // 首先过滤出已发布的智能体
      const publishedAgents = (this.agents || []).filter(agent =>
        agent.publishStatus === 'PUBLISHED'
      );

      // 如果没有搜索文本，返回所有已发布的智能体
      if (!this.searchText) return publishedAgents;

      // 根据搜索文本进一步过滤
      return publishedAgents.filter(agent =>
        agent.agentName.toLowerCase().includes(this.searchText.toLowerCase()) ||
        (agent.description && agent.description.toLowerCase().includes(this.searchText.toLowerCase()))
      );
    },

    filteredSharedAgents() {
      // 暂时保留空数组，共享智能体逻辑可以后续添加
      return [];
    },
    
    scrollHeight() {
      const baseHeight = 300;
      const itemCount = this.filteredMyAgents.length + this.filteredSharedAgents.length;
      return Math.min(400, baseHeight + itemCount * 10);
    },
    
    agentSectionTitle() {
      if (!this.chat) return '智能体';

      if (this.chat.type === 'PRIVATE') {
        // 私聊显示对方的智能体
        return `${this.chat.targetName || '对方'}的智能体`;
      } else if (this.chat.type === 'GROUP') {
        // 群聊显示群成员智能体
        return '群成员智能体';
      }

      return '智能体';
    }
  },
  methods: {

    handleStartChat(agent) {
      console.log('[ChatAgentSide] 🎯 handleStartChat 被调用，agent:', agent?.agentName);
      // 新API返回的智能体都是可用的，不需要检查 isPublished

      console.log('[ChatAgentSide] ✅ 触发 start-agent-chat 事件');
      // 触发父组件的层级对话模式
      this.$emit('start-agent-chat', {
        agent: agent,
        chat: this.chat,
        member: null  // 如果是自己的智能体，member为null
      });
    },

    async handleAgentAction({ command, agent }) {
      console.log('[ChatAgentSide] 处理智能体操作:', command, agent.agentName);

      if (command === 'share') {
        // 打开分享对话框
        this.currentShareAgent = agent;
        this.shareDialogVisible = true;
      } else if (command === 'details') {
        // 查看详情：获取分享链接并在新窗口打开
        try {
          const response = await this.$http({
            url: `/agent/share`,
            method: 'post',
            data: { agentId: agent.agentId }
          });

          if (response && response.shareUrl) {
            // 在新窗口打开分享链接
            window.open(response.shareUrl, '_blank');
          } else {
            this.$message.error('获取智能体链接失败');
          }
        } catch (err) {
          console.error('[ChatAgentSide] 获取分享链接失败:', err);
          this.$message.error(err.response?.data?.msg || '获取智能体链接失败，请稍后重试');
        }
      } else if (command === 'edit') {
        // 编辑智能体
        this.$emit('edit-agent', agent);
      } else if (command === 'start') {
        // 启动服务
        this.$emit('start-agent-service', agent);
      } else if (command === 'stop') {
        // 停止服务
        this.$emit('stop-agent-service', agent);
      } else if (command === 'restart') {
        // 重启服务
        this.$emit('restart-agent-service', agent);
      } else if (command === 'delete') {
        // 删除智能体
        this.$emit('delete-agent', agent);
      }
    },

    refreshAgents() {
      // 通知父组件刷新
      this.$emit('refresh-agents');
    }
  }
}
</script>

<style lang="scss">
.chat-agent-side {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fafafa;

  .agent-side-search {
    padding: 10px;
    flex-shrink: 0;
  }

  .agent-side-scrollbar {
    flex: 1;
    min-height: 0; // 重要：让flex子元素可以收缩
    overflow: hidden;
  }

  .agent-side-list {
    padding: 10px;
    
    .agent-section {
      margin-bottom: 20px;
      
      .section-title {
        font-size: 14px;
        font-weight: bold;
        color: #666;
        margin-bottom: 10px;
        padding: 8px 0;
        border-bottom: 1px solid #f0f0f0;
        display: flex;
        align-items: center;
        
        &::before {
          content: '';
          width: 4px;
          height: 14px;
          background-color: #1890ff;
          margin-right: 8px;
          border-radius: 2px;
        }
      }
      
      .agent-side-member {
        margin-bottom: 8px;
        background-color: white;
        border-radius: 6px;
        border: 1px solid #f0f0f0;
        
        &:hover {
          border-color: #d9d9d9;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
      }
    }
    
    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: #999;
      
      i {
        font-size: 48px;
        margin-bottom: 16px;
        display: block;
      }
      
      p {
        margin: 0 0 16px 0;
        font-size: 14px;
      }
    }
  }

  // 加载状态样式
  .el-loading-mask {
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 6px;
  }
}
</style>